import { useState, useEffect, useRef } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import {
  PanelLeftClose,
  PanelLeftOpen,
  ExternalLink,
  RefreshCw,
  FolderTree,
  Eye,
  Loader2,
  CheckCircle2,
  XCircle,
  Hammer,
  Rocket,
  Terminal,
} from 'lucide-react';
import { ChatPanel } from '@/components/chat/ChatPanel';
import { FileExplorer } from '@/components/files/FileExplorer';
import { CodeViewer } from '@/components/files/CodeViewer';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { projectsApi } from '@/api/projects';
import { useChatStore, type BuildStatus, type DeployStatus, type CommandOutput } from '@/stores/chatStore';
import type { Project, FileNode } from '@/types';

type RightPanelTab = 'preview' | 'files';

interface LocationState {
  initialConversationId?: string;
  initialPrompt?: string;
}

export function ProjectChatPage() {
  const { project } = useOutletContext<{ project: Project }>();
  const location = useLocation();
  const locationState = location.state as LocationState | null;

  // Get initialConversationId from navigation state (preferred) or project
  const initialConversationId = locationState?.initialConversationId || project.initialConversationId;
  const initialPrompt = locationState?.initialPrompt || project.description;

  // Debug logging
  console.log('[ProjectChatPage] Rendering', {
    projectId: project.id,
    projectSlug: project.slug,
    projectInitialConversationId: project.initialConversationId,
    locationStateInitialConversationId: locationState?.initialConversationId,
    finalInitialConversationId: initialConversationId,
    initialPrompt: initialPrompt?.substring(0, 50),
    locationState,
  });

  // Get build/deploy state from store
  const {
    buildStatus,
    deployStatus,
    thinkingStatus,
    terminalOutput,
    fileOperations,
    lastFileChange,
  } = useChatStore();

  // Panel state
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState<RightPanelTab>('preview');
  const [previewKey, setPreviewKey] = useState(0);

  // File explorer state
  const [files, setFiles] = useState<FileNode[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | undefined>();
  const [fileContent, setFileContent] = useState<string>('');
  const [fileContentLoading, setFileContentLoading] = useState(false);

  // Track last file change to auto-refresh
  const lastFileChangeRef = useRef<Date | null>(null);

  // Load files when switching to files tab
  useEffect(() => {
    if (rightPanelTab === 'files' && files.length === 0) {
      loadFiles();
    }
  }, [rightPanelTab]);

  // Auto-refresh files when new files are created
  useEffect(() => {
    if (lastFileChange && lastFileChange !== lastFileChangeRef.current) {
      lastFileChangeRef.current = lastFileChange;
      // Debounce file refresh
      const timer = setTimeout(() => {
        loadFiles();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [lastFileChange]);

  // Auto-refresh preview when deploy succeeds
  useEffect(() => {
    if (deployStatus === 'success') {
      // Wait a moment for deployment to stabilize, then refresh
      const timer = setTimeout(() => {
        setPreviewKey((prev) => prev + 1);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [deployStatus]);

  const loadFiles = async () => {
    setFilesLoading(true);
    try {
      const fileList = await projectsApi.getFiles(project.slug);
      setFiles(fileList);
    } catch (error) {
      console.error('Failed to load files:', error);
      setFiles([]);
    } finally {
      setFilesLoading(false);
    }
  };

  const loadFileContent = async (path: string) => {
    setFileContentLoading(true);
    try {
      const result = await projectsApi.getFileContent(project.slug, path);
      setFileContent(result.content);
    } catch (error) {
      console.error('Failed to load file content:', error);
      setFileContent(`// Error loading file: ${path}`);
    } finally {
      setFileContentLoading(false);
    }
  };

  const handleSelectFile = (path: string) => {
    setSelectedFile(path);
    loadFileContent(path);
  };

  const handleRefreshPreview = () => {
    setPreviewKey((prev) => prev + 1);
  };

  const handleSaveFile = async (content: string) => {
    console.log('Saving file:', selectedFile, content);
  };

  return (
    <div className="flex h-full bg-gray-50 dark:bg-dark-900">
      {/* Left Panel - Chat */}
      <div
        className={cn(
          'flex flex-col border-r border-gray-200 dark:border-dark-700 transition-all duration-300',
          rightPanelCollapsed ? 'flex-1' : 'w-1/2 min-w-[400px]'
        )}
      >
        <ChatPanel
          projectId={project.id}
          initialConversationId={initialConversationId}
          initialPrompt={initialPrompt}
          className="flex-1"
        />
      </div>

      {/* Right Panel - Preview / Files */}
      <div
        className={cn(
          'flex flex-col bg-white dark:bg-dark-800 transition-all duration-300',
          rightPanelCollapsed ? 'w-0 overflow-hidden' : 'flex-1 min-w-[400px]'
        )}
      >
        {/* Right Panel Header */}
        <div className="h-12 border-b border-gray-200 dark:border-dark-700 flex items-center justify-between px-3 flex-shrink-0">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setRightPanelTab('preview')}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                rightPanelTab === 'preview'
                  ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700'
              )}
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>
            <button
              onClick={() => setRightPanelTab('files')}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                rightPanelTab === 'files'
                  ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700'
              )}
            >
              <FolderTree className="w-4 h-4" />
              Files
            </button>
          </div>

          <div className="flex items-center gap-2">
            {rightPanelTab === 'preview' && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefreshPreview}
                  title="Refresh preview"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
                {project.previewUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(project.previewUrl!, '_blank')}
                    title="Open in new tab"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                )}
              </>
            )}
            {rightPanelTab === 'files' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={loadFiles}
                title="Refresh files"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Right Panel Content */}
        <div className="flex-1 overflow-hidden">
          {rightPanelTab === 'preview' ? (
            <PreviewPanel
              key={previewKey}
              previewUrl={project.previewUrl}
              projectName={project.name}
              status={project.status}
              buildStatus={buildStatus}
              deployStatus={deployStatus}
              thinkingStatus={thinkingStatus}
              terminalOutput={terminalOutput}
            />
          ) : (
            <FilesPanel
              files={files}
              loading={filesLoading}
              selectedFile={selectedFile}
              fileContent={fileContent}
              fileContentLoading={fileContentLoading}
              recentlyChangedFiles={fileOperations.map((op) => ({
                path: op.path,
                timestamp: op.timestamp,
              }))}
              onSelectFile={handleSelectFile}
              onSaveFile={handleSaveFile}
              onRefresh={loadFiles}
            />
          )}
        </div>
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
        className="absolute right-4 top-[calc(50%+56px)] -translate-y-1/2 z-10 p-2 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
        title={rightPanelCollapsed ? 'Show preview panel' : 'Hide preview panel'}
      >
        {rightPanelCollapsed ? (
          <PanelLeftOpen className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        ) : (
          <PanelLeftClose className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        )}
      </button>
    </div>
  );
}

function PreviewPanel({
  previewUrl,
  projectName,
  status,
  buildStatus,
  deployStatus,
  thinkingStatus,
  terminalOutput,
}: {
  previewUrl?: string;
  projectName: string;
  status: string;
  buildStatus: BuildStatus;
  deployStatus: DeployStatus;
  thinkingStatus: string | null;
  terminalOutput: CommandOutput[];
}) {
  const terminalRef = useRef<HTMLDivElement>(null);

  // Auto-scroll terminal
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminalOutput]);

  // Check if we're in a building state
  const isBuilding = buildStatus === 'building' || deployStatus === 'deploying';
  const hasBuildActivity = buildStatus !== 'idle' || deployStatus !== 'idle' || thinkingStatus;

  // Show building UI when building/deploying
  if (isBuilding || (hasBuildActivity && !previewUrl)) {
    return (
      <div className="h-full flex flex-col bg-gray-900">
        {/* Build Status Header */}
        <div className="px-4 py-3 bg-gray-800 border-b border-gray-700 flex items-center gap-3">
          {buildStatus === 'building' && (
            <>
              <Hammer className="w-5 h-5 text-yellow-400 animate-pulse" />
              <span className="text-yellow-400 font-medium">Building your app...</span>
            </>
          )}
          {deployStatus === 'deploying' && (
            <>
              <Rocket className="w-5 h-5 text-blue-400 animate-bounce" />
              <span className="text-blue-400 font-medium">Deploying...</span>
            </>
          )}
          {buildStatus === 'success' && deployStatus !== 'deploying' && (
            <>
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-medium">Build complete</span>
            </>
          )}
          {buildStatus === 'failed' && (
            <>
              <XCircle className="w-5 h-5 text-red-400" />
              <span className="text-red-400 font-medium">Build failed</span>
            </>
          )}
          {!buildStatus || (buildStatus === 'idle' && thinkingStatus) ? (
            <>
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
              <span className="text-gray-400 font-medium">{thinkingStatus || 'Processing...'}</span>
            </>
          ) : null}
        </div>

        {/* Terminal Output */}
        <div className="flex-1 overflow-hidden">
          <div className="px-4 py-2 bg-gray-800 border-b border-gray-700 flex items-center gap-2">
            <Terminal className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-400">Build Output</span>
          </div>
          <div
            ref={terminalRef}
            className="h-full overflow-y-auto p-4 font-mono text-sm"
          >
            {terminalOutput.length === 0 ? (
              <div className="text-gray-500">Waiting for output...</div>
            ) : (
              terminalOutput.map((entry, idx) => (
                <div key={idx} className="mb-3">
                  <div className="text-cyan-400 mb-1">$ {entry.command}</div>
                  <pre className="text-gray-300 whitespace-pre-wrap break-all">
                    {entry.output}
                  </pre>
                  {entry.exitCode !== undefined && entry.exitCode !== 0 && (
                    <div className="text-red-400 mt-1">
                      Exit code: {entry.exitCode}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  // Deploy success - show preview
  if (deployStatus === 'success' && previewUrl) {
    return (
      <div className="h-full flex flex-col">
        <div className="px-3 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
          <span className="text-sm text-green-700 dark:text-green-400 font-medium">
            Deployment successful!
          </span>
        </div>
        <div className="px-3 py-2 bg-gray-50 dark:bg-dark-900 border-b border-gray-200 dark:border-dark-700">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700">
            <span className="text-xs text-gray-400">https://</span>
            <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
              {previewUrl.replace('https://', '')}
            </span>
          </div>
        </div>
        <div className="flex-1 relative">
          <iframe
            src={previewUrl}
            className="absolute inset-0 w-full h-full border-0"
            title={`${projectName} preview`}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </div>
      </div>
    );
  }

  // No preview URL
  if (!previewUrl) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-dark-700 flex items-center justify-center mb-4">
          <Eye className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Ready to Build
        </h3>
        <p className="text-gray-500 dark:text-gray-400 max-w-sm">
          {status === 'Running'
            ? 'Your project is running but the preview URL is not set.'
            : 'Start chatting with the AI to build your project. You\'ll see live progress here as files are created.'}
        </p>
      </div>
    );
  }

  // Default preview
  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-2 bg-gray-50 dark:bg-dark-900 border-b border-gray-200 dark:border-dark-700">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700">
          <span className="text-xs text-gray-400">https://</span>
          <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
            {previewUrl.replace('https://', '')}
          </span>
        </div>
      </div>
      <div className="flex-1 relative">
        <iframe
          src={previewUrl}
          className="absolute inset-0 w-full h-full border-0"
          title={`${projectName} preview`}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      </div>
    </div>
  );
}

interface RecentlyChangedFile {
  path: string;
  timestamp: Date;
}

function FilesPanel({
  files,
  loading,
  selectedFile,
  fileContent,
  fileContentLoading,
  recentlyChangedFiles = [],
  onSelectFile,
  onSaveFile,
  onRefresh,
}: {
  files: FileNode[];
  loading: boolean;
  selectedFile?: string;
  fileContent: string;
  fileContentLoading: boolean;
  recentlyChangedFiles?: RecentlyChangedFile[];
  onSelectFile: (path: string) => void;
  onSaveFile: (content: string) => void;
  onRefresh: () => void;
}) {
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-dark-700 flex items-center justify-center mb-4">
          <FolderTree className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          No Files Yet
        </h3>
        <p className="text-gray-500 dark:text-gray-400 max-w-sm mb-4">
          Start chatting with the AI to generate code for your project.
        </p>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <div className="w-56 border-r border-gray-200 dark:border-dark-700 flex-shrink-0 overflow-y-auto">
        <FileExplorer
          files={files}
          selectedFile={selectedFile}
          recentlyChangedFiles={recentlyChangedFiles}
          onSelectFile={onSelectFile}
          className="h-full"
        />
      </div>
      <div className="flex-1 overflow-hidden">
        {selectedFile ? (
          fileContentLoading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <CodeViewer
              path={selectedFile}
              content={fileContent}
              onSave={onSaveFile}
              onClose={() => {}}
              className="h-full"
            />
          )
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400">
            Select a file to view its contents
          </div>
        )}
      </div>
    </div>
  );
}
